import { useState, useEffect } from 'react';
import { AssetSummary } from '@/types/crypto';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  BarChart3, 
  Brain,
  ExternalLink,
  AlertCircle,
  Activity,
  Users,
  Zap,
  Target
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';
import { useAssetAnalysis, useDerivativesData } from '@/hooks/useCryptoAssets';
import { formatUSD, formatPrice, formatPercent, getChangeColor } from '@/lib/format';

interface AssetDrawerProps {
  asset: AssetSummary | null;
  isOpen: boolean;
  onClose: () => void;
  autoAnalyze?: boolean;
}

export const AssetDrawer = ({ asset, isOpen, onClose, autoAnalyze = false }: AssetDrawerProps) => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [hasAutoAnalyzed, setHasAutoAnalyzed] = useState(false);
  const { data: analysis, isLoading: analysisLoading, refetch } = useAssetAnalysis(asset);
  const { data: derivativesData } = useDerivativesData(asset?.symbol || '');

  // Auto-analyze when drawer opens with autoAnalyze flag
  useEffect(() => {
    if (autoAnalyze && isOpen && asset && !analysis && !hasAutoAnalyzed && !analysisLoading) {
      setHasAutoAnalyzed(true);
      setIsAnalyzing(true);
      refetch().finally(() => setIsAnalyzing(false));
    }
  }, [autoAnalyze, isOpen, asset, analysis, hasAutoAnalyzed, analysisLoading, refetch]);

  // Reset auto-analyze state when drawer closes
  useEffect(() => {
    if (!isOpen) {
      setHasAutoAnalyzed(false);
    }
  }, [isOpen]);

  if (!asset) return null;

  const handleAnalysis = async () => {
    setIsAnalyzing(true);
    try {
      await refetch();
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-2xl">
        <SheetHeader>
          <div className="flex items-center gap-3">
            {asset.image ? (
              <img 
                src={asset.image} 
                alt={asset.name}
                className="w-12 h-12 rounded-full"
              />
            ) : (
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-sm font-bold text-primary">
                  {asset.symbol.slice(0, 2)}
                </span>
              </div>
            )}
            <div>
              <SheetTitle className="text-xl">{asset.name}</SheetTitle>
              <SheetDescription>
                #{asset.rank} • {asset.symbol} • Análise detalhada
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-120px)] mt-6">
          <div className="space-y-6">
            {/* Price Section */}
            <Card className="glass-card">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-xl bg-primary/10 flex items-center justify-center">
                    <DollarSign className="h-5 w-5 text-primary" />
                  </div>
                  Preço Atual
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center space-y-3">
                  <div className="text-3xl font-bold">{formatPrice(asset.price)}</div>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="text-center">
                      <div className="text-xs text-muted-foreground">1 Hora</div>
                      <div className={`font-semibold ${getChangeColor(asset.change_1h)}`}>
                        {formatPercent(asset.change_1h)}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-muted-foreground">24 Horas</div>
                      <div className={`font-semibold ${getChangeColor(asset.change_24h)}`}>
                        {formatPercent(asset.change_24h)}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-muted-foreground">7 Dias</div>
                      <div className={`font-semibold ${getChangeColor(asset.change_7d)}`}>
                        {formatPercent(asset.change_7d)}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Market Metrics */}
            <Card className="glass-card">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-xl bg-accent/10 flex items-center justify-center">
                    <BarChart3 className="h-5 w-5 text-accent" />
                  </div>
                  Métricas de Mercado
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Market Cap</span>
                    <span className="font-semibold">{formatUSD(asset.market_cap)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">FDV</span>
                    <span className="font-semibold">{formatUSD(asset.fdv)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Volume 24h</span>
                    <span className="font-semibold">{formatUSD(asset.volume_24h)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* DeFi Metrics */}
            {(asset.tvl || asset.revenue_30d || asset.fees_7d) && (
              <Card className="glass-card">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-xl bg-success/10 flex items-center justify-center">
                      <Zap className="h-5 w-5 text-success" />
                    </div>
                    DeFi & Fundamentals
                    {asset.defi_category && (
                      <Badge variant="outline" className="ml-auto text-xs">
                        {asset.defi_category}
                      </Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {asset.tvl && (
                       <>
                         <div className="flex justify-between items-center">
                           <div className="flex items-center gap-1">
                             <span className="text-muted-foreground">TVL</span>
                             <span className="text-xs text-muted-foreground ml-1" title={`Fonte: ${asset.tvl_source_label || 'DeFiLlama'}`}>ℹ️</span>
                           </div>
                           <span className="font-semibold text-blue-600">{formatUSD(asset.tvl)}</span>
                         </div>
                        {asset.tvl_change_7d && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">TVL Δ 7d</span>
                            <span className={`font-semibold ${getChangeColor(asset.tvl_change_7d)}`}>
                              {formatPercent(asset.tvl_change_7d)}
                            </span>
                          </div>
                        )}
                      </>
                    )}
                    
                    {/* Fees Extended Section */}
                    {asset.fees_7d && (
                      <>
                        <Separator />
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Fees 7d</span>
                          <span className="font-semibold text-green-600">{formatUSD(asset.fees_7d)}</span>
                        </div>
                        {asset.fees_30d && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Fees 30d</span>
                            <span className="font-semibold">{formatUSD(asset.fees_30d)}</span>
                          </div>
                        )}
                        {asset.fees_all_time && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Fees All Time</span>
                            <span className="font-semibold text-primary">{formatUSD(asset.fees_all_time)}</span>
                          </div>
                        )}
                        {asset.fees_change_1d != null && (
                          <div className="flex justify-between items-center">
                            <span className="text-muted-foreground">Fees Δ 24h</span>
                            <Badge 
                              variant="outline" 
                              className={asset.fees_change_1d >= 0 
                                ? "bg-profit/10 text-profit border-profit/30" 
                                : "bg-loss/10 text-loss border-loss/30"
                              }
                            >
                              {asset.fees_change_1d >= 0 ? '↑' : '↓'} {formatPercent(Math.abs(asset.fees_change_1d))}
                            </Badge>
                          </div>
                        )}
                      </>
                    )}
                    
                    {asset.revenue_30d && (
                      <>
                        <Separator />
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Revenue 30d</span>
                          <span className="font-semibold text-purple-600">{formatUSD(asset.revenue_30d)}</span>
                        </div>
                        {asset.revenue_change_30d && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Revenue Δ 30d</span>
                            <span className={`font-semibold ${getChangeColor(asset.revenue_change_30d)}`}>
                              {formatPercent(asset.revenue_change_30d)}
                            </span>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Derivatives Data */}
            {derivativesData && (
              <Card className="glass-card">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-xl bg-destructive/10 flex items-center justify-center">
                      <Target className="h-5 w-5 text-destructive" />
                    </div>
                    Derivativos
                  </CardTitle>
                </CardHeader>
                <CardContent>
                   <div className="space-y-3">
                     {derivativesData.funding_8h !== undefined && (
                       <div className="flex justify-between items-center">
                         <div className="flex items-center gap-1">
                           <span className="text-muted-foreground">Funding 8h</span>
                           <span className="text-xs text-muted-foreground ml-1" title="Taxa paga a cada 8h em futuros perpétuos. Positiva = compradores pagam vendedores (otimismo)">ℹ️</span>
                         </div>
                         <div className="text-right">
                           <span className={`text-sm font-semibold ${getChangeColor(derivativesData.funding_8h)}`}>
                             {formatPercent(derivativesData.funding_8h, 3)}
                           </span>
                           {derivativesData.funding_trend_7d !== undefined && (
                             <div className="text-xs text-muted-foreground">
                               7d: {derivativesData.funding_trend_7d > 0 ? '+' : ''}{derivativesData.funding_trend_7d.toFixed(3)}%
                             </div>
                           )}
                         </div>
                       </div>
                     )}
                     {derivativesData.open_interest && (
                       <div className="flex justify-between items-center">
                         <div className="flex items-center gap-1">
                           <span className="text-muted-foreground">Open Interest</span>
                           <span className="text-xs text-muted-foreground ml-1" title="Total de contratos em aberto. Maior OI = mais especulação/interesse institucional">ℹ️</span>
                         </div>
                         <span className="font-semibold">{formatUSD(derivativesData.open_interest)}</span>
                       </div>
                     )}
                     {derivativesData.oi_change_24h !== undefined && (
                       <div className="flex justify-between">
                         <span className="text-muted-foreground">OI Δ 24h</span>
                         <span className={`text-sm ${getChangeColor(derivativesData.oi_change_24h)}`}>
                           {formatPercent(derivativesData.oi_change_24h)}
                         </span>
                       </div>
                     )}
                   </div>
                </CardContent>
              </Card>
            )}

            {/* Alerts */}
            {asset.alerts && asset.alerts.length > 0 && (
              <Card className="glass-card border-destructive/20">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-xl bg-destructive/10 flex items-center justify-center">
                      <AlertCircle className="h-5 w-5 text-destructive" />
                    </div>
                    Alertas Ativos
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {asset.alerts.map((alert: any, index: number) => (
                      <Badge 
                        key={index} 
                        variant={alert.severity === 'high' ? 'destructive' : 'secondary'}
                        className="block p-3 text-left"
                      >
                        <div className="flex items-center gap-2">
                          {alert.severity === 'high' ? '⚡' : '🔔'}
                          <span className="font-medium">{alert.type.replace(/_/g, ' ')}</span>
                        </div>
                        {alert.payload?.message && (
                          <div className="text-xs mt-1 opacity-90">
                            {alert.payload.message}
                          </div>
                        )}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* AI Analysis */}
            <Card className="glass-card">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Brain className="h-5 w-5 text-primary" />
                  </div>
                  Análise com IA
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!analysis && !analysisLoading && (
                  <div className="text-center space-y-3">
                    <p className="text-muted-foreground">
                      Obtenha uma análise detalhada com IA conservadora
                    </p>
                    <Button 
                      onClick={handleAnalysis}
                      disabled={isAnalyzing}
                      className="w-full"
                    >
                      <Activity className="h-4 w-4 mr-2" />
                      {isAnalyzing ? 'Analisando...' : 'Gerar Análise'}
                    </Button>
                  </div>
                )}
                
                {(analysisLoading || isAnalyzing) && (
                  <div className="space-y-3">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-4 w-1/2" />
                  </div>
                )}

                {analysis && (
                  <div className="space-y-4">
                    {/* Render Markdown analysis */}
                    <div className="prose prose-sm max-w-none dark:prose-invert">
                      <ReactMarkdown 
                        rehypePlugins={[rehypeSanitize]}
                        components={{
                          a: ({ href, children, ...props }) => (
                            <a 
                              href={href} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-primary hover:underline"
                              {...props}
                            >
                              {children}
                            </a>
                          )
                        }}
                      >
                        {analysis.analysis || 'Análise não disponível'}
                      </ReactMarkdown>
                    </div>
                    
                    {/* Sources */}
                    {analysis.sources && analysis.sources.length > 0 && (
                      <div>
                        <p className="text-sm font-medium mb-2">Fontes:</p>
                        <div className="flex flex-wrap gap-1">
                          {analysis.sources.map((source: string, index: number) => (
                            <Badge key={index} variant="outline" className="text-xs">
                              {source}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Disclaimer */}
                    <div className="text-xs text-muted-foreground p-2 bg-muted/30 rounded">
                      {analysis.disclaimer || 'Análise baseada em dados públicos. Não constitui recomendação financeira.'}
                    </div>

                    <Button 
                      onClick={handleAnalysis}
                      variant="outline"
                      size="sm"
                      className="w-full"
                      disabled={isAnalyzing}
                    >
                      Atualizar Análise
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* External Links */}
            <Card className="glass-card">
              <CardHeader className="pb-3">
                <CardTitle>Links Externos</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-2">
                  <Button variant="outline" size="sm" asChild>
                    <a 
                      href={`https://www.coingecko.com/en/coins/${asset.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2"
                    >
                      <ExternalLink className="h-3 w-3" />
                      CoinGecko
                    </a>
                  </Button>
                  <Button variant="outline" size="sm" asChild>
                    <a 
                      href={`https://coinmarketcap.com/currencies/${asset.id}/`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2"
                    >
                      <ExternalLink className="h-3 w-3" />
                      CMC
                    </a>
                  </Button>
                  <Button variant="outline" size="sm" asChild>
                    <a 
                      href={`https://defillama.com/protocol/${asset.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2"
                    >
                      <ExternalLink className="h-3 w-3" />
                      DeFiLlama
                    </a>
                  </Button>
                  <Button variant="outline" size="sm" asChild>
                    <a 
                      href={`https://www.tradingview.com/symbols/${asset.symbol}USD/`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2"
                    >
                      <ExternalLink className="h-3 w-3" />
                      TradingView
                    </a>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};
